import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, Alert, Button, CircularProgress } from '@mui/material';
import { useAuthStore } from '@/stores/auth.store';
import { authService } from '@/lib/auth.service';

const LoginContainer = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ display: 'flex', minHeight: '100vh', width: '100%', bgcolor: '#ffffff' }}>
    <Box
      sx={{
        width: '65%',
        minHeight: '100vh',
        background: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)),
            url('https://images.unsplash.com/photo-1534438327276-14e5300c3a48?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: { xs: 'none', md: 'block' },
      }}
    />
    <Box
      sx={{
        width: { xs: '100%', md: '35%' },
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 5,
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 400 }}>
        {children}
      </Box>
    </Box>
  </Box>
);

/**
 * SSO Page - Handles auto-login from external applications
 *
 * Usage from external app:
 *   window.open('https://aviron-mdm.com/sso?token=<access_token>')
 *
 * Or as a link:
 *   <a href="https://aviron-mdm.com/sso?token=<access_token>">Open Aviron MDM</a>
 *
 * The token should be a valid Casdoor JWT access token.
 */
export function SSOPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setAuth, isAuthenticated } = useAuthStore();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const validateAndLogin = useCallback(async (token: string, redirectPath: string) => {
    setIsLoading(true);
    setError(null);

    try {
      // Validate the token with backend
      const tokenResponse = await authService.validateSSOToken(token);

      // Set auth state
      setAuth(tokenResponse);

      // Clear URL params and redirect
      window.history.replaceState({}, '', '/sso');
      navigate(redirectPath, { replace: true });
    } catch (err) {
      console.error('SSO validation error:', err);
      setError('Invalid or expired token. Please login again.');
      setIsLoading(false);
    }
  }, [setAuth, navigate]);

  useEffect(() => {
    // If already authenticated, redirect to dashboard
    if (isAuthenticated) {
      navigate('/', { replace: true });
      return;
    }

    const token = searchParams.get('token');
    const redirect = searchParams.get('redirect') || '/';

    if (!token) {
      queueMicrotask(() => {
        setError('No token provided. Please use a valid SSO link.');
        setIsLoading(false);
      });
      return;
    }

    // Validate and exchange the token
    queueMicrotask(() => validateAndLogin(token, redirect));
  }, [searchParams, isAuthenticated, navigate, validateAndLogin]);

  const handleGoToLogin = () => {
    navigate('/login', { replace: true });
  };

  return (
    <LoginContainer>
      {isLoading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
          <CircularProgress size={48} sx={{ color: '#FF6720' }} />
          <Typography variant="h5" sx={{ fontWeight: 500, color: '#161616' }}>
            Signing you in...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please wait while we verify your credentials
          </Typography>
        </Box>
      ) : error ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
          <Alert
            severity="error"
            sx={{ width: '100%' }}
          >
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              SSO Login Failed
            </Typography>
            {error}
          </Alert>
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleGoToLogin}
            sx={{ height: 48, bgcolor: '#FF6720', '&:hover': { bgcolor: '#F5490F' } }}
          >
            Go to Login Page
          </Button>
        </Box>
      ) : null}
    </LoginContainer>
  );
}
